/**
 * Onboarding API Service
 * Epic 13: Central de Ajuda e Suporte
 * Story 13.4: Onboarding Guiado - API Client
 *
 * 15 API endpoints for onboarding tour and checklist management
 */

import api from "./api";

// ============================================================================
// Types
// ============================================================================

export interface OnboardingProgress {
  id: string;
  user_id: string;
  company_id: string;
  tour_id: string;
  tour_version: string;
  status: "not_started" | "in_progress" | "completed" | "skipped";
  current_step: number | null;
  completed_steps: string[];
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
  is_complete: boolean;
  completion_percentage: number;
}

export interface TourStepButton {
  text: string;
  action: string;
  classes?: string;
}

export interface TourStepAttachTo {
  element: string;
  on: "top" | "bottom" | "left" | "right";
}

export interface TourStep {
  id: string;
  title: string;
  text: string;
  attachTo?: TourStepAttachTo;
  manual_section_id?: string;
  buttons: TourStepButton[];
  classes?: string;
  scrollTo?: boolean;
  when?: Record<string, unknown>;
}

export interface TourDefinition {
  tour_id: string;
  title: string;
  description: string;
  target_roles: string[];
  version: string;
  steps: TourStep[];
}

export interface ChecklistItem {
  id: string;
  user_id: string;
  company_id: string;
  checklist_item_id: string;
  is_completed: boolean;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ChecklistDefinitionItem {
  id: string;
  title: string;
  description: string;
}

export interface ShouldShowResponse {
  should_show: boolean;
  tour_id: string;
  reason: "first_login" | "not_started" | "completed" | "skipped" | "version_updated";
}

export interface TourAnalytics {
  total_users: number;
  not_started: number;
  in_progress: number;
  completed: number;
  skipped: number;
  completion_rate: number;
  average_steps_completed: number;
}

// ============================================================================
// API Methods
// ============================================================================

/**
 * AC1: Check if onboarding modal should be shown
 * Called on first login and app initialization
 */
export const shouldShowTour = async (): Promise<ShouldShowResponse> => {
  const response = await api.get("/onboarding/should-show");
  return response.data;
};

/**
 * AC5: List all available tours
 */
export const getAllTours = async (): Promise<TourDefinition[]> => {
  const response = await api.get("/onboarding/tours");
  return response.data;
};

/**
 * AC2, AC5: Get specific tour definition by ID
 */
export const getTourDefinition = async (tourId: string): Promise<TourDefinition> => {
  const response = await api.get(`/onboarding/tours/${tourId}`);
  return response.data;
};

/**
 * AC2: Get tour definition for user role
 */
export const getTourForRole = async (role: string): Promise<TourDefinition> => {
  const response = await api.get(`/onboarding/tours/role/${role}`);
  return response.data;
};

/**
 * AC2: Get user's progress for a tour
 */
export const getProgress = async (tourId: string): Promise<OnboardingProgress> => {
  const response = await api.get(`/onboarding/progress/${tourId}`);
  return response.data;
};

/**
 * AC2: Create new progress record
 */
export const createProgress = async (data: {
  tour_id: string;
  tour_version: string;
  status?: string;
  current_step?: number;
  completed_steps?: string[];
}): Promise<OnboardingProgress> => {
  const response = await api.post("/onboarding/progress", data);
  return response.data;
};

/**
 * AC2, AC3: Update tour progress
 */
export const updateProgress = async (
  tourId: string,
  data: {
    status?: string;
    current_step?: number;
    completed_steps?: string[];
  }
): Promise<OnboardingProgress> => {
  const response = await api.patch(`/onboarding/progress/${tourId}`, data);
  return response.data;
};

/**
 * AC3: Skip tour (pular tour)
 */
export const skipTour = async (tourId: string): Promise<OnboardingProgress> => {
  const response = await api.post(`/onboarding/progress/${tourId}/skip`);
  return response.data;
};

/**
 * AC4: Reset tour (refazer tour posteriormente)
 */
export const resetTour = async (tourId: string): Promise<OnboardingProgress> => {
  const response = await api.post(`/onboarding/progress/${tourId}/reset`);
  return response.data;
};

/**
 * AC2: Complete tour
 */
export const completeTour = async (tourId: string): Promise<OnboardingProgress> => {
  const response = await api.post(`/onboarding/progress/${tourId}/complete`);
  return response.data;
};

/**
 * AC6: Get user's checklist items
 */
export const getChecklistItems = async (): Promise<ChecklistItem[]> => {
  const response = await api.get("/onboarding/checklist");
  return response.data;
};

/**
 * AC6: Mark checklist item as completed
 */
export const completeChecklistItem = async (itemId: string): Promise<ChecklistItem> => {
  const response = await api.post(`/onboarding/checklist/${itemId}/complete`);
  return response.data;
};

/**
 * AC6: Get all checklist definitions
 */
export const getAllChecklistDefinitions = async (): Promise<
  Record<string, ChecklistDefinitionItem[]>
> => {
  const response = await api.get("/onboarding/checklist/definitions");
  return response.data;
};

/**
 * AC6: Get checklist definition for specific role
 */
export const getChecklistDefinitionForRole = async (
  role: string
): Promise<ChecklistDefinitionItem[]> => {
  const response = await api.get(`/onboarding/checklist/definitions/${role}`);
  return response.data;
};

/**
 * AC7 (Optional): Get tour analytics (admin only)
 */
export const getTourAnalytics = async (tourId: string): Promise<TourAnalytics> => {
  const response = await api.get(`/onboarding/analytics/${tourId}`);
  return response.data;
};

export default {
  shouldShowTour,
  getAllTours,
  getTourDefinition,
  getTourForRole,
  getProgress,
  createProgress,
  updateProgress,
  skipTour,
  resetTour,
  completeTour,
  getChecklistItems,
  completeChecklistItem,
  getAllChecklistDefinitions,
  getChecklistDefinitionForRole,
  getTourAnalytics,
};
